<template>
  <aside class="app-sidebar p-6 h-full" id="sidebar" :class="{ active: active }">
    <div class="mt-8 pl-9 app-sidebar-inner fixed">
      <!-- Start::main-sidebar-header -->
			<div class="main-sidebar-header">
				<client-only>
				<template v-if="avatarUrl">
					<img :src="avatarUrl" class="w-[48px] h-[48px] rounded-full filled-upload" alt=""/>
				</template>
				<template v-else>
					<DefaultAvatar :width="48" :height="48" :width-inner="48" :height-inner="48" class="empty-upload" />
				</template>
				<div class="main-sidebar-header-content">
					<h4 class="main-sidebar-header-user">{{ auth.user.name }}</h4>
				</div>
				</client-only>
			</div>

      <!-- End::main-sidebar-header -->

      <!-- Start::Navigation Links -->
      <div class="main-sidebar flex flex-col space-y-3">

        <div class="flex items-center mt-[34px] mb-[3px]">
          <span class="main-sidebar-account">{{ t('auth.account') }}</span>
        </div>

        <nav class="pl-[12px]">
          <div v-for="section in accountSections" :key="section.id">
            <NuxtLink
              :to="sectionLink(section.id)"
              class="sidebar-nav-item mb-[8px]"
              :class="{ 'active-item': isSectionActive(section.id) }"
              @click="openSection(section.id)"
            >
              {{ t(section.label) }}
            </NuxtLink>
          </div>
<!--          <div v-if="auth.user.slug">
            <a :href="`/profile/${auth.user.slug}`"
               class="sidebar-nav-item" v-if="auth.user.slug"
            >
              {{ t('auth.view_public_profile') }}
            </a>
          </div>
          <div v-else>
            <a href="#"
               class="sidebar-nav-item"
            >
              {{ t('auth.view_public_profile') }}
            </a>
          </div>-->
        </nav>

				<div class="flex flex-col !mt-[23px] mb-6">
					<Divider class="mt-0 mb-[15px] md:divider-light"/>
				</div>

				<!-- Community header -->
				<div class="flex items-center mt-[24px] mb-[4px]">
					<span class="main-sidebar-account">{{ t('community.title') }}</span>
				</div>

				<!-- Community nav items -->
				<nav class="pl-[12px] mb-6">
					<div>
						<NuxtLink
							:to="localePath('/community/members')"
							class="sidebar-nav-item mb-[8px]"
							active-class="active-item"
						>
							{{ t('community.members') }}
						</NuxtLink>
					</div>
					<div>
						<NuxtLink
							:to="localePath('/grants')"
							class="sidebar-nav-item mb-[8px]"
							:class="{ 'active-item': isGrantsSection }"
						>
							{{ t('funding.sidebar') }}
						</NuxtLink>
					</div>
<!--					<div v-else>
						<NuxtLink
							:to="localePath('/community/funding')"
							class="sidebar-nav-item mb-[8px]"
							active-class="active-item"
						>
							{{ t('funding.sidebar') }}
						</NuxtLink>
					</div>-->
					<div>
<!--						<NuxtLink
							:to="localePath('/community/resources')"
							class="sidebar-nav-item mb-[8px]"
							active-class="active-item"
						>
							{{ t('community.resources') }}
						</NuxtLink>-->
						<NuxtLink
							:to="localePath('/community/resources')"
							class="sidebar-nav-item mb-[8px]"
							active-class="active-item"
						>
							{{ t('community.resources') }}
						</NuxtLink>
					</div>
				</nav>

				<template v-if="can('dashboard.access') || hasRole('admin')">
					<div class="flex flex-col !mt-[23px] mb-6">
						<Divider class="mt-0 mb-[15px] md:divider-light"/>
					</div>

					<div class="flex flex-col mb-0">
						<p class="mt-0 mb-0 flex items-center">
							<span class="flex items-center">
							<a href="https://dashboard.media4change.co/" class="flex items-center cursor-pointer logout" target="_blank">
								<span class="mr-[9px] flex items-center">
									<img src="@/assets/images/elements/users.svg" :alt="t('auth.manage_users')" class="w-[16px] -ml-[1px] -mt-[4px]"/>
								</span>
								{{ t('auth.manage_users') }}
							</a>
							</span>
						</p>
					</div>
				</template>


        <div class="flex flex-col !mt-[23px] mb-6">
          <Divider class="mt-0 mb-[15px] md:divider-light"/>
        </div>

        <!-- Sign-out Button -->
        <div class="flex flex-col mb-0 pb-20">
          <p class="mt-0 mb-0 flex items-center">
            <span class="flex items-center">
            <a @click="signOut" class="flex items-center cursor-pointer logout">
              <span class="mr-[9px] flex items-center">
                <img src="@/assets/images/elements/logout.svg" :alt="t('auth.logout')" class="w-[16px] -ml-[1px] -mt-[4px]"/>
              </span>
              {{ t('auth.logout') }}
            </a>
            </span>
          </p>
        </div>
      </div>
      <!-- End::Navigation Links -->
    </div>

  </aside>
</template>


<script setup lang="ts">
import DefaultAvatar from "@/components/avatar/DefaultAvatar.vue";
import { useAcl } from "@/core/composables/useAcl";
import { useLocalePath } from '#imports'
import { useAccountSectionNav } from '@/composables/account';

const nuxtApp = useNuxtApp();
const storage = nuxtApp.$storage as (path: string) => string;
const auth = useAuthStore();
const { t } = useI18n();
const route = useRoute();
const localePath = useLocalePath();
const { can, hasRole } = useAcl();
const props = defineProps({
  active: {
    type: Boolean,
    default: true,
  },
});

const avatarUrl = computed<string | null>(() => {
	return auth.user.avatar
		? storage(auth.user.avatar)
		: null;
});

const grantsRoot = localePath('/grants')
const isGrantsSection = computed(() => {
	return route.path.startsWith(grantsRoot + '/') || route.path === grantsRoot
})

const { sections: accountSections, sectionLink, isSectionActive, openSection } =
	useAccountSectionNav(() => props.active)

const signOut = () => {
  if (auth.logged) {
    auth.logout();
  } else {
    nuxtApp.runWithContext(() => {
      return navigateTo('/auth/login');
    });
  }
};
</script>


<style lang="scss">
.sidebar-nav-item {
  font-size: 16px;
  color: #4a4a4a;
  font-weight: 400;
  padding: 4px 12px;
  width: auto;
  display: inline-block;
  text-decoration: none;
  gap: 16px;
  border-radius: 4px;
}

.sidebar-nav-item.active-item {
  background: rgba(255, 255, 255, 0.5);
}

.sidebar-nav-item:hover {

}


</style>
